import React from 'react';

import {
    Box, Button, Stack, Dialog,
    DialogActions, DialogContent,
    DialogTitle,
    Typography,
    Divider,
    MenuItem,
    Grid,
    TextField,
    InputAdornment,
    Tooltip,
    IconButton,
    LinearProgress
} from '@mui/material';

import dayjs from 'dayjs';
import JsonFormatter from 'react-json-formatter';

import { jsonViewStyle } from './styles/theme';

import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import { DatePicker } from '@mui/x-date-pickers';
import { CustomDataGrid } from './components/CustomDataGrid';

import ApplicationContext from './contexts/application';
import ErrorDialog from './components/ErrorDialog';
import ConfirmDialog from './components/ConfirmDialog';
import AppLog from './admin/applog';

import { getFilteredLogs, getLogTypes } from './libs/apiCalls';

function Logs() {
    let defaultPageSize = 20;

    const { settings, setDialog } = React.useContext(ApplicationContext);
    const [showError, setShowError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [gridFilter, setGridFilter] = React.useState([]);
    const [dialogContent, setDialogContent] = React.useState('');
    const [openInfoDialog, setOpenInfoDialog] = React.useState(false);
    const [searchString, setSearchString] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);

    const [types, setTypes] = React.useState([]);
    const [selectedType, setSelectedType] = React.useState('');
    const [gridRows, setGridRows] = React.useState([]);
    const [serverData, setServerData] = React.useState({
        records: [],
        currentPage: 0,
        totalPages: 0,
        totalRecords: 0
    });

    const filter = React.useRef({
        searchString: '',
        fromDate: '',
        toDate: '',
        type: ''
    });

    const columns = [
        {
            field: 'referenceId', headerName: 'Ref', flex: 0.2,
            renderCell: (props) => <>{props.row.logId}</>
        },
        {
            field: 'date', headerName: 'Date', flex: 0.3,
            valueFormatter: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss') 
        },
        { field: 'message', headerName: 'Message', flex: 1 },
        { field: 'source', headerName: 'Source', flex: 0.2 },
        { field: 'type', headerName: 'Type', flex: 0.2 }
    ];

    const getData = (filter, page, pageSize) => {
        setLoading(true);

        getFilteredLogs(settings.token, JSON.stringify(filter), page, pageSize, 'Date', 1,
            (response) => {
                if (!response.data.error) {
                    setServerData({
                        records: JSON.parse(response.data.result),
                        currentPage: response.data.currentPage,
                        totalPages: response.data.totalPages,
                        totalRecords: response.data.totalRecords
                    });

                    setGridRows(JSON.parse(response.data.result));
                }
                else {
                    setDialog(response.data.errors[0], true);
                }
                
                setLoading(false);
            },
            (error) => {
                setErrorMessage(error.message);
                setShowError(true);
                setLoading(false);
            }
        );
    };

    const getTypes = () => {
        setLoading(true);

        getLogTypes(settings.token,
            (response) => {
                if (!response.data.error) {
                    setTypes(JSON.parse(response.data.result));
                }
                else {
                    setDialog(response.data.errors[0], true);
                }
                                
                setLoading(false);
            },
            (error) => {
                setErrorMessage(error.message);
                setShowError(true);
                setLoading(false);
            }
        );
    };

    const handlePageModelChanged = (event) => {
        setPage(event.page);
        setPageSize(event.pageSize);

        getData(filter.current, event.page, event.pageSize);
    };

    const typeItems = (types) => {
        var items = [];
        items.push(
            <MenuItem key={-1} value=''><em>All</em></MenuItem>
        );

        types.forEach((item, index) => {
            items.push(
                <MenuItem key={index} value={item}>{item}</MenuItem>
            );
        });

        return items;
    };

    const handleFilterChange = (event) => {
        filter.current[event.target.name] = event.target.value;

        if (event.target.name === 'type') {
            setSelectedType(event.target.value);
        }
        else if (event.target.name === 'searchString') {
            setSearchString(event.target.value);
        }
    }

    const handleFilterDateChange = (target, date) => {
        filter.current[target] = date ? dayjs(date.$d).format('YYYY-MM-DD') : '';
    };

    const handleFilter = () => {
        getData(filter.current, page, pageSize);
    };

    const handleClearFilter = () => {
        filter.current.searchString = '';
        filter.current.fromDate = '';
        filter.current.toDate = '';
        filter.current.type = '';

        setSelectedType('');
        setSearchString('');
        getData(filter.current, page, pageSize);
    };

    React.useEffect(() => {
        if (Object.keys(settings).length > 0 && gridRows.length === 0) {
            if (types.length === 0) {
                getTypes();
            }
        }
    }, [settings]);

    React.useEffect(() => {
        if (Object.keys(settings).length > 0) {
            getData(filter.current, page, pageSize);
        }
    }, [types]);

    return (
        <>
            <Box sx={{ mb: 2, height: '60px' }}>
                <Box sx={{ width: '50%' }}>
                    <Typography sx={{ marginLeft: '5px', fontWeight: '400', color: 'text.bright' }} style={{ fontSize: '25px' }}>Application Log</Typography>
                    <Typography sx={{ marginLeft: '5px', fontWeight: '300', color: 'text.secondary' }} style={{ fontSize: '15px' }}>All system informational and error messages.</Typography>
                </Box>
                <Box sx={{ justifyContent: 'flex-end', width: '50%', mb: 8 }}>
                </Box>
            </Box>
            <Divider sx={{ mb: 4 }} />
            <Grid container gap={2} sx={{ width: '100%' }}>
                <Grid item gap={2} sx={{ display: 'flex', width: '100%' }}>
                    <TextField
                        variant='outlined'
                        name='searchString'
                        size='small'
                        placeholder='Search for any attribute or value'
                        InputProps={{
                            startAdornment: <InputAdornment position='start'><SearchIcon /></InputAdornment>
                        }}
                        value={searchString}
                        onChange={handleFilterChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        sx={{ width: '40%' }} />

                    <DatePicker
                        name='fromDate'
                        slotProps={{
                            textField: {
                                size: 'small',
                                placeholder: 'From Date'
                            }
                        }}
                        sx={{
                            width: '15%'
                        }}
                        onChange={(date) => handleFilterDateChange('fromDate', date)}
                        value={filter.current.fromDate ? dayjs(filter.current.fromDate) : null}
                    />

                    <DatePicker
                        name='toDate'
                        slotProps={{
                            textField: {
                                size: 'small',
                                placeholder: 'To Date'
                            }
                        }}
                        sx={{
                            width: '15%'
                        }}
                        onChange={(date) => handleFilterDateChange('toDate', date)}
                        value={filter.current.toDate ? dayjs(filter.current.toDate) : null}
                    />

                    <TextField
                        name='type'
                        size='small'
                        select
                        sx={{
                            width: '25%',
                            '& .MuiSelect-select span::before': {
                                content: "'Type'",
                            },
                        }}
                        value={selectedType}
                        onChange={handleFilterChange}
                    >
                        {typeItems(types)}
                    </TextField>

                    <Tooltip title="Apply filter">
                        <IconButton
                            onClick={handleFilter}
                            color='primary'
                            sx={{ mt: -1, ml: -1 }}
                        >
                            <FilterAltIcon fontSize="large" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Clear filters">
                        <IconButton
                            onClick={handleClearFilter}
                            color='primary'
                            sx={{ mt: -1, ml: -1 }}
                        >
                            <FilterAltOffIcon fontSize="large" />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', flexDirection: 'column', width: '100%' }} >
                    <LinearProgress sx={{ width: '100%', display: loading ? 'block' : 'none' }} />
                    <CustomDataGrid
                        columns={columns}
                        paginationMode='server'
                        rows={gridRows}
                        rowCount={serverData.totalRecords}

                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: defaultPageSize,
                                },
                            },
                        }}

                        slotProps={{
                            pagination: {
                                showFirstButton: true,
                                showLastButton: true
                            }
                        }}
                        
                        pagination
                        pageSizeOptions={[5, 10, 20, 50]}
                        onPaginationModelChange={handlePageModelChanged}

                        disableRowSelectionOnClick
                        getRowId={(row) => row.logId}
                        onCellClick={(data) => {
                            if (data.field === 'message') {
                                setDialogContent(data.row.message);
                                setOpenInfoDialog(true);
                            }
                        }}

                        filterModel={{ items: gridFilter }}
                        onFilterModelChange={(filter) => setGridFilter(filter)}
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                        }

                        disableColumnFilter
                        autoHeight
                    />

                    <ErrorDialog open={showError} title={'Application Error'}
                        content={errorMessage}
                        onOkClick={() => setShowError(false)} />

                    <Dialog
                        open={openInfoDialog}
                        maxWidth='lg'
                        fullWidth={true}
                        PaperProps={{
                            sx: {
                                height: '50vh'
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                setOpenInfoDialog(false);
                            }
                        }}
                    >
                        <DialogTitle>Message Detail</DialogTitle>
                        <DialogContent>
                            <div className={'overflow-div'}>
                                <JsonFormatter json={dialogContent}
                                    tabWith={4}
                                    jsonStyle={jsonViewStyle}
                                />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenInfoDialog(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            </Grid>
        </>

    );
}

export default Logs;